import { FC } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer as MuiDrawer, IconButton } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';

import { elevations } from '../../styles';
import Alert from '../Alert';
import { DrawerProps } from './types';

const Drawer: FC<React.PropsWithChildren<DrawerProps>> = ({
  alertProps,
  children,
  footerContent,
  isOpen,
  onClose,
  width,
}) => {
  const theme = useTheme();

  return (
    <MuiDrawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      ModalProps={{
        BackdropProps: {
          sx: {
            bgcolor: alpha(theme.palette.colors.slate[800], 0.2),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px 0 0 16px',
          boxShadow: elevations.z5,
          width: width || '628px',
        },
      }}
    >
      <InnerDrawer>
        {alertProps && (
          <AlertContainer>
            <Alert isFullWidth {...alertProps} />
          </AlertContainer>
        )}
        <IconButton
          onClick={onClose}
          aria-label='close drawer'
          sx={{
            alignItems: 'center',
            background: theme.palette.colors.white,
            borderRadius: '50%',
            boxShadow: elevations.z1,
            display: 'flex',
            height: '40px',
            justifyContent: 'center',
            position: 'fixed',
            right: '40px',
            top: '40px',
            width: '40px',
            zIndex: 1,
          }}
        >
          <CloseButtonIcon />
        </IconButton>
        <ScrollShadowContainer>{children}</ScrollShadowContainer>
        {footerContent && <Footer>{footerContent}</Footer>}
      </InnerDrawer>
    </MuiDrawer>
  );
};

const AlertContainer = styled('div')`
  left: 32px;
  position: absolute;
  right: 32px;
  top: 24px;
  z-index: 2;
`;

const CloseButtonIcon = styled(CloseIcon)`
  color: ${p => p.theme.palette.colors.black};
  font-size: 30px;
`;

const InnerDrawer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const Footer = styled('div')`
  background-color: ${p => p.theme.palette.colors.white};
  padding: 24px 32px;
`;

const ScrollShadowContainer = styled('div')`
  /*
  Shadows when container is scrolled
  Four gradients for top cover, bottom cover, top shadow, bottom shadow
  Example here: https://codepen.io/chriscoyier/pen/YzXBYvL
  */
  background:
    linear-gradient(${p => p.theme.palette.colors.white} 30%, transparent)
      center top,
    linear-gradient(transparent, ${p => p.theme.palette.colors.white} 70%)
      center bottom,
    radial-gradient(
        farthest-side at 50% 0,
        ${p => alpha(p.theme.palette.colors.black, 0.2)},
        transparent
      )
      center top,
    radial-gradient(
        farthest-side at 50% 100%,
        ${p => alpha(p.theme.palette.colors.black, 0.2)},
        transparent
      )
      center bottom;
  background-attachment: local, local, scroll, scroll;
  background-repeat: no-repeat;
  background-size:
    100% 40px,
    100% 40px,
    100% 14px,
    100% 14px;
  flex: 1;
  overflow: hidden scroll;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export default Drawer;
